/**
 * 业务参数（`#business-params`，后台对齐批 5b）：业务包里的业务参数与平台口径只读展示——每项写含义、当前值、被哪些运行规则读。
 * 当前值由后端从业务包 pack.json 现读（单一出处），本页不另存；读它的规则点了去「运行规则」打开那条（`?rule=`）。
 * 本期由平台代维护，改动走版本（改业务包、走 PR）。业务包里不在本页的节（身份、拒绝说法、信息项……）页尾指到对应页面。
 */
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { Card, CardContent } from "@/components/ui/card";
import { Drawer, DrawerSection } from "@/components/ui/drawer";
import { EmptyState, LoadingInline } from "@/components/ui/feedback";
import { type BusinessParam, planErrorMessage, usePlanBusinessParams } from "@/lib/api/plan-lab";

import { FailState } from "../kit/fail-state";
import { PageJump } from "../kit/page-jump";
import { type PlanColumn, PlanTable } from "../kit/plan-table";

/** 规则编号：点了去「运行规则」页并打开那条 */
function RuleLink({ id }: { id: string }) {
	return (
		<Link
			to="/plan-lab"
			search={{ rule: id }}
			hash="guards"
			title={`去运行规则看 ${id}`}
			onClick={(e) => e.stopPropagation()}
			className="font-mono text-xs text-text-secondary underline-offset-2 hover:text-brand-accent hover:underline"
			data-testid="plan-biz-param-rule-link"
		>
			{id}
		</Link>
	);
}

function scalar(v: unknown): string {
	if (v === null || v === undefined || v === "") return "（空）";
	if (typeof v === "boolean") return v ? "是" : "否";
	return typeof v === "string" ? v : JSON.stringify(v);
}

/** 嵌套对象的一层摘要：键=值、键=值（值里再有对象就只写项数，全文在抽屉里） */
function inner(o: Record<string, unknown>): string {
	return Object.entries(o)
		.map(([k, x]) =>
			x && typeof x === "object" ? `${k}（${Object.keys(x).length} 项）` : `${k}=${scalar(x)}`,
		)
		.join("、");
}

/** 当前值一行写法（表格里用）：标量原样；列表用「、」连；对象写成「键：键=值、键=值」按键分号隔开，不再截成半个 JSON */
function oneLine(v: unknown): string {
	if (Array.isArray(v))
		return v.map((x) => (typeof x === "object" ? JSON.stringify(x) : scalar(x))).join("、");
	if (v && typeof v === "object")
		return Object.entries(v as Record<string, unknown>)
			.map(([k, x]) =>
				x && typeof x === "object" && !Array.isArray(x)
					? `${k}：${inner(x as Record<string, unknown>)}`
					: `${k}=${Array.isArray(x) ? x.map((y) => scalar(y)).join("、") : scalar(x)}`,
			)
			.join("；");
	return scalar(v);
}

/** 当前值完整写法（抽屉里用）：对象逐键一行 */
function ValueBlock({ v }: { v: unknown }) {
	if (v && typeof v === "object" && !Array.isArray(v)) {
		return (
			<dl className="space-y-1" data-testid="plan-biz-param-value">
				{Object.entries(v as Record<string, unknown>).map(([k, x]) => (
					<div key={k} className="flex gap-3">
						<dt className="w-40 shrink-0 font-mono text-xs text-text-muted">{k}</dt>
						<dd className="min-w-0 break-all font-mono text-xs text-text-primary">{oneLine(x)}</dd>
					</div>
				))}
			</dl>
		);
	}
	if (Array.isArray(v) && v.some((x) => typeof x === "object")) {
		return (
			<ul className="space-y-1" data-testid="plan-biz-param-value">
				{v.map((x) => (
					<li key={JSON.stringify(x)} className="break-all font-mono text-xs">
						{oneLine(x)}
					</li>
				))}
			</ul>
		);
	}
	return (
		<span className="break-all font-mono text-xs" data-testid="plan-biz-param-value">
			{oneLine(v)}
		</span>
	);
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div className="space-y-1">
			<div className="text-xs text-text-muted">{label}</div>
			<div className="whitespace-pre-wrap text-sm text-text-primary">{children}</div>
		</div>
	);
}

const columns: PlanColumn<BusinessParam>[] = [
	{
		key: "name",
		title: "参数",
		className: "w-56",
		render: (p) => (
			<span className="block">
				<span className="block text-text-primary">{p.name}</span>
				<span className="block font-mono text-xs text-text-muted">{p.path}</span>
			</span>
		),
	},
	{
		key: "meaning",
		title: "含义",
		className: "max-w-[360px]",
		render: (p) => (
			<span className="block truncate text-text-secondary" title={p.meaning}>
				{p.meaning}
			</span>
		),
	},
	{
		key: "value",
		title: "当前值",
		className: "max-w-[280px]",
		render: (p) => (
			<span className="block truncate font-mono text-xs" title={oneLine(p.value)}>
				{oneLine(p.value)}
			</span>
		),
	},
	{
		key: "rules",
		title: "被哪些运行规则读",
		className: "max-w-[260px]",
		render: (p) =>
			p.rules.length ? (
				<span className="flex flex-wrap gap-x-2 gap-y-1">
					{p.rules.map((r) => (
						<RuleLink key={r} id={r} />
					))}
				</span>
			) : (
				"—"
			),
	},
];

export function BizParamsTab() {
	const q = usePlanBusinessParams();
	const [open, setOpen] = useState<BusinessParam | null>(null);
	const data = q.data;
	const groups = useMemo(
		() =>
			(data?.groups ?? []).map((g) => ({
				...g,
				rows: (data?.params ?? []).filter((p) => p.group === g.id),
			})),
		[data],
	);

	if (q.isLoading)
		return (
			<Card data-testid="plan-block-biz-params">
				<CardContent className="py-4">
					<LoadingInline />
				</CardContent>
			</Card>
		);
	if (q.isError || !data)
		return (
			<Card data-testid="plan-block-biz-params">
				<CardContent>
					<FailState
						reason={planErrorMessage(q.error)}
						onRetry={() => q.refetch()}
						retrying={q.isFetching}
						testId="plan-biz-params-fail"
					/>
				</CardContent>
			</Card>
		);

	return (
		<div className="space-y-3" data-testid="plan-biz-params">
			{groups.map((g) => (
				<Card key={g.id} data-testid={`plan-biz-params-group-${g.id}`}>
					<CardContent className="space-y-3 py-4">
						<div className="flex items-baseline gap-3">
							<span className="text-sm font-medium text-text-primary">{g.name}</span>
							<span className="text-xs text-text-muted">{g.desc}</span>
							<span className="ml-auto text-xs text-text-muted">{g.rows.length} 项</span>
						</div>
						{g.rows.length ? (
							<PlanTable
								columns={columns}
								rows={g.rows}
								rowKey={(p) => p.path}
								onRowClick={setOpen}
								density="compact"
								rowTestId="plan-biz-param-row"
							/>
						) : (
							<EmptyState title="这一组没有参数" desc="业务包里没有登记这一组。" />
						)}
					</CardContent>
				</Card>
			))}
			<div className="text-xs text-text-muted" data-testid="plan-biz-params-elsewhere">
				业务包里另几节不在本页：身份与提示词变量见 <PageJump to="prompts" />
				；拒绝说法、业务对象说明见 <PageJump to="signal" />
				；信息项与用法标记、意图表见 <PageJump to="tags" />
				；办成用语等固定说法（业务包语言包，只留状态词与旁检词表）暂无单独页面，用到它们的规则在{" "}
				<PageJump to="guards" />。
			</div>
			<Drawer
				open={!!open}
				onClose={() => setOpen(null)}
				width="min(720px,95vw)"
				testId="plan-biz-param-drawer"
				title={open?.name}
				description={open ? `业务包 pack.json · ${open.path}` : undefined}
			>
				{open ? (
					<>
						<DrawerSection title="是什么">
							<Field label="含义">{open.meaning}</Field>
							{open.note ? <Field label="业务包里的说明">{open.note}</Field> : null}
						</DrawerSection>
						<DrawerSection title="当前值">
							<ValueBlock v={open.value} />
						</DrawerSection>
						<DrawerSection title="谁在读">
							<Field label="运行规则">
								{open.rules.length ? (
									<span className="flex flex-wrap gap-2">
										{open.rules.map((r) => (
											<RuleLink key={r} id={r} />
										))}
									</span>
								) : (
									"无"
								)}
							</Field>
							<Field label="还在哪用">{open.used || "—"}</Field>
						</DrawerSection>
					</>
				) : null}
			</Drawer>
		</div>
	);
}
